// Nav notification bell — unread badge + recent-notifications dropdown.
//
// Shown only when a user is logged in (rendered conditionally by the nav).
// Polls the lightweight unread_count endpoint every 60s while the tab is
// visible (paused via the Page Visibility API to avoid background churn).
// Clicking the bell opens a dropdown of the 10 most recent notifications;
// clicking a row marks it read and navigates to the related post.
//
// Brand: teal/emerald with an amber-free red badge per spec (bg-red-500).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, User } from 'lucide-react';
import {
  fetchUnreadCount,
  fetchList,
  markRead,
  markAllRead,
  timeAgo,
  notificationMessage,
} from '../lib/notifications';

const POLL_MS = 60000;

const NotificationBell = ({ onOpenNotification, onSeeAll }) => {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const refreshCount = useCallback(async () => {
    setUnread(await fetchUnreadCount());
  }, []);

  // Poll unread count every 60s, pausing while the tab is hidden.
  useEffect(() => {
    refreshCount();
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (!document.hidden) refreshCount();
      }, POLL_MS);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.hidden) { stop(); } else { refreshCount(); start(); }
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [refreshCount]);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    const { notifications } = await fetchList({ limit: 10 });
    setItems(notifications);
    setLoading(false);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadRecent();
  };

  const handleRowClick = async (n) => {
    if (!n.read_at) {
      await markRead(n.id);
      setItems(prev => prev.map(x => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
      setUnread(c => Math.max(0, c - 1));
    }
    setOpen(false);
    if (onOpenNotification) onOpenNotification(n);
  };

  const handleMarkAll = async () => {
    await markAllRead();
    setItems(prev => prev.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    setUnread(0);
  };

  const badge = unread > 99 ? '99+' : String(unread);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={toggleOpen}
        className="relative p-2 rounded-md text-gray-600 hover:text-teal-700 hover:bg-teal-50 transition-colors"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
            data-testid="notification-badge"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] bg-white border border-teal-100 rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-teal-50">
            <span className="text-sm font-semibold text-teal-800">Notifications</span>
            {unread > 0 && (
              <span className="text-[11px] font-medium text-teal-600">{unread} unread</span>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400" data-testid="notification-empty">
                You're all caught up.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleRowClick(n)}
                  className={`w-full flex items-start gap-2.5 px-4 py-3 text-left border-b border-gray-50 hover:bg-teal-50/60 transition-colors ${
                    n.read_at ? 'bg-white' : 'bg-teal-50/40'
                  }`}
                  data-testid={`notification-row-${n.id}`}
                >
                  <span className="flex-shrink-0 mt-0.5 h-7 w-7 rounded-full bg-teal-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-teal-700" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs text-gray-700 leading-snug">{notificationMessage(n)}</span>
                    <span className="block text-[11px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</span>
                  </span>
                  {!n.read_at && <span className="flex-shrink-0 mt-1.5 h-2 w-2 rounded-full bg-teal-500" />}
                </button>
              ))
            )}
          </div>

          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50">
            <button
              onClick={handleMarkAll}
              className="text-[11px] font-medium text-teal-600 hover:text-teal-800 disabled:text-gray-300"
              disabled={unread === 0}
              data-testid="notification-mark-all"
            >
              Mark all as read
            </button>
            <button
              onClick={() => { setOpen(false); if (onSeeAll) onSeeAll(); }}
              className="text-[11px] font-medium text-teal-600 hover:text-teal-800"
              data-testid="notification-see-all"
            >
              See all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
